import { ChangeEventHandler, FC, useEffect, useState } from 'react';
import {
    CountryCode,
    getCountries,
    getCountryCallingCode,
    AsYouType,
    validatePhoneNumberLength,
} from 'libphonenumber-js';
import {
    Box,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    SelectProps,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FlagIcon } from 'src/components/flag-icon';
import { Language, CountryCodes } from 'src/shared/enum/Countries';

import { styles } from './styles';

import type { PhoneInputProps } from './types';

const PHONE_COUNTRIES_MAP = getCountries();
const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

PHONE_COUNTRIES_MAP.sort((a, b) => {
    if (a === CountryCodes.UK) return -1;
    if (b === CountryCodes.UK) return 1;

    const aName = regionNames.of(a) as string;
    const bName = regionNames.of(b) as string;

    return aName.localeCompare(bName);
});

export const PhoneInput: FC<PhoneInputProps> = ({
    value,
    error,
    helperText,
    label,
    required,
    onChange,
}) => {
    const { i18n, t } = useTranslation();
    const currentLanguage = i18n.language;

    const defaultCountryCode: CountryCode =
        currentLanguage === Language.EN ? CountryCodes.UK : CountryCodes.SPAIN;

    const [countryCode, setCountryCode] = useState<CountryCode>(defaultCountryCode);

    const { countryCode: userCountryCode = defaultCountryCode, number = '' } = value;

    const callingCode = getCountryCallingCode(countryCode);

    const inputValue = number || '';

    useEffect(() => {
        if (userCountryCode) {
            setCountryCode(userCountryCode);
        }
    }, [userCountryCode]);

    const handleChangeCountry: SelectProps<CountryCode>['onChange'] = (event) => {
        const newCountryCode = event.target.value as CountryCode;
        const newCallingCode = getCountryCallingCode(newCountryCode);

        setCountryCode(newCountryCode);
        onChange({
            countryCode: newCountryCode,
            callingCode: newCallingCode,
            number: '',
        });
    };

    const handleChangeNumber: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
        const newNumber = target.value;

        if (!countryCode) {
            onChange({
                countryCode: undefined,
                callingCode: undefined,
                number: newNumber,
            });
            return;
        }

        const formatter = new AsYouType(countryCode);
        formatter.input(newNumber);

        const formatterNumber = formatter.getNumberValue() || '';
        const lengthStatus = validatePhoneNumberLength(formatterNumber, countryCode);

        if (lengthStatus !== 'TOO_LONG') {
            onChange({
                countryCode,
                callingCode,
                number: formatter.getNationalNumber(),
            });
        }
    };

    return (
        <FormControl css={styles.container}>
            {label}
            <Box css={styles.inputWrapper}>
                <Select
                    value={countryCode || ''}
                    onChange={handleChangeCountry}
                    variant="standard"
                    css={styles.select}
                    error={error}
                >
                    {PHONE_COUNTRIES_MAP.map((code) => (
                        <MenuItem key={code} value={code}>
                            {code && <FlagIcon code={code} />}

                            <Typography
                                className="phone-input__country-name"
                                css={styles.optionText}
                            >
                                {regionNames.of(code)}
                            </Typography>
                        </MenuItem>
                    ))}
                </Select>

                <TextField
                    type="tel"
                    required={required}
                    value={inputValue}
                    onChange={handleChangeNumber}
                    error={error}
                    variant="standard"
                    fullWidth
                    css={styles.input}
                    placeholder={t('your_details_form_phone_label')}
                />
            </Box>

            {error && (
                <FormHelperText variant="standard" error>
                    {helperText}
                </FormHelperText>
            )}
        </FormControl>
    );
};
